import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  AreaChart, Area, BarChart, Bar, ComposedChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer
} from 'recharts';
import { finance as financeApi } from '../../api/endpoints';
import { useNotification } from '../../hooks';
import { formatCurrency, formatDate } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import {
  KpiCard, PageHeader, Select, Notification, Button, Input, SubTabs
} from '../../components/ui';
import type { FinanceDashboard, FinancePieSlice, FinanceSource } from '../../types';
import {
  PERIODS, RANGE_PRESETS, SOURCE_OPTIONS, SLICE_COLORS,
  compactAxisTick, financialYearOf, financialYearOptions, presetRange,
  readSources, rememberSources, hasNoBudgetData, pieTotal, spentPercent,
  budgetHealth, percentChange
} from './analytics';
import type { RangePreset } from './analytics';

const INCOME = '#00ACC1';
const EXPENSE = '#E91E63';
const NET = '#7C4DFF';
const DONATIONS = '#7C4DFF';
const COLLECTIONS = '#FF8A00';
const CASH = '#2E7D32';

const EMPTY: FinanceDashboard = {
  kpi: { income: 0, expense: 0, net: 0 },
  chartData: [],
  pieData: [],
  incomeData: [],
  topPayees: [],
  budgetHeadsData: [],
  yoyBudgetData: []
};

const FinancialDashboard = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  const yearOptions = useMemo(() => financialYearOptions(), []);
  const [finYear, setFinYear] = useState(() => financialYearOf(new Date()));
  const [period, setPeriod] = useState<string>('Monthly');

  /** What the sheet covers — printed under the masthead so it stands alone. */
  const printReference = `FY ${finYear}  •  ${period}`;
  const [preset, setPreset] = useState<RangePreset>('fy');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [sources, setSources] = useState<FinanceSource[]>(readSources);
  const [data, setData] = useState<FinanceDashboard>(EMPTY);
  const [isSyncing, setIsSyncing] = useState(false);

  const { notification, notify, dismiss } = useNotification();

  const range = useMemo(() => presetRange(preset, custom), [preset, custom]);

  const load = useCallback(async () => {
    // A half-typed custom range asks for nothing until both dates are in.
    if (preset === 'custom' && !range) return;
    setIsSyncing(true);
    try {
      const result = await financeApi.dashboard({ finYear, period, ...range, sources });
      setData({ ...EMPTY, ...result });
    } catch (err) {
      setData(EMPTY);
      notify(err.message || 'Could not load financial analytics.', 'error');
    } finally {
      setIsSyncing(false);
    }
    // `notify` is stable; refetch only when the selection changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finYear, period, preset, range, sources]);

  useEffect(() => { load(); }, [load]);

  const toggleSource = (key: FinanceSource) => {
    setSources((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      rememberSources(next);
      return next;
    });
  };

  const { kpi, chartData, pieData, budgetHeadsData, yoyBudgetData } = data;
  const incomeData = data.incomeData || [];
  const topPayees = data.topPayees || [];
  const sourceTotals = data.sourceTotals || {};
  const totalExpensePie = pieTotal(pieData);
  const totalIncomePie = pieTotal(incomeData);
  const rangeLabel = data.range?.label || `FY ${finYear}`;
  const previousLabel = data.previousRange?.label || 'previous period';

  // Recharts needs concrete colours, so the axes/tooltips are themed by hand.
  const axisStyle = { fill: t.textMuted, fontSize: 12 };
  const gridStroke = t.border;
  const tooltipStyle = {
    backgroundColor: t.bgMain,
    borderColor: t.borderStrong,
    color: t.textMain,
    borderRadius: '8px'
  };
  const money = (value) => formatCurrency(Number(value));
  const cursorFill = { fill: t.isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' };

  const panel: CSSProperties = {
    backgroundColor: t.bgSubtle,
    padding: t.spacing.lg,
    borderRadius: t.radius.xl,
    border: `1px solid ${t.border}`
  };
  const panelTitle: CSSProperties = {
    color: t.textMain,
    margin: `0 0 ${t.spacing.lg} 0`,
    fontSize: t.fontSize.base,
    fontWeight: t.fontWeight.bold
  };
  const emptyNote: CSSProperties = {
    color: t.textFaint, fontSize: t.fontSize.md,
    fontStyle: 'italic', textAlign: 'center', padding: '40px 0'
  };
  const pill = (selected: boolean): CSSProperties => ({
    padding: '6px 12px', minHeight: '34px', borderRadius: t.radius.pill, cursor: 'pointer',
    fontWeight: 'bold', fontSize: t.fontSize.sm, whiteSpace: 'nowrap',
    border: `1px solid ${selected ? t.primary : t.border}`,
    backgroundColor: selected ? t.primarySoft : t.bgMain,
    color: selected ? t.textAccent : t.textMuted
  });

  /** "▲ 12% vs FY 2025-26 (₹x)" under a KPI, or "no earlier figure". */
  const comparison = (current: number, previous?: number, invert = false): ReactNode => {
    const change = percentChange(current, previous);
    if (change === null) {
      return <span style={{ color: t.textFaint }}>no figure for {previousLabel}</span>;
    }
    // For expenses, going up is the bad direction.
    const good = invert ? change <= 0 : change >= 0;
    return (
      <span style={{ color: good ? t.success : t.danger }}>
        {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(1)}% vs {previousLabel}
        <span style={{ color: t.textFaint }}> ({formatCurrency(previous)})</span>
      </span>
    );
  };



  /** A ranked list panel: the biggest few of a breakdown, with share bars. */
  const topList = (title: string, slices: FinancePieSlice[], colour: string, empty: string) => {
    const total = pieTotal(slices);
    const top = slices.slice(0, 5);
    return (
      <div style={panel}>
        <h3 style={panelTitle}>{title}</h3>
        {top.length === 0 ? <div style={emptyNote}>{empty}</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {top.map((row, i) => {
              const share = total ? (row.value / total) * 100 : 0;
              return (
                <div key={row.name}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: t.fontSize.md }}>
                    <span style={{ color: t.textMain, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ color: t.textFaint, marginRight: '6px' }}>{i + 1}.</span>{row.name}
                    </span>
                    <span style={{ color: t.textMain, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {formatCurrency(row.value)}
                      <span style={{ color: t.textFaint, fontWeight: 'normal', fontSize: t.fontSize.xs }}> {share.toFixed(0)}%</span>
                    </span>
                  </div>
                  <div style={{ backgroundColor: t.bgMain, height: '6px', borderRadius: '3px', overflow: 'hidden', marginTop: '4px' }}>
                    <div style={{ width: `${share}%`, height: '100%', backgroundColor: colour }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  /**
   * Recharts draws legend labels in its own grey, which sits too close to the
   * panel colour in dark mode — the "Surplus / Deficit" label in particular
   * was barely readable (CHM-116). Labels take the app's text colour instead.
   */
  const legendLabel = (value: React.ReactNode) => (
    <span style={{ color: t.textMain }}>{value}</span>
  );

  /** A donut with its total in the middle and a value legend on the right. */
  const donut = (title: string, slices: FinancePieSlice[], total: number, empty: string) => (
    <div style={panel}>
      {/* The total also sits in the heading. Inside the donut hole a full
          rupee figure runs past the 110px opening and clipped, so the number
          the panel exists to show was the hardest thing on it to read
          (CHM-129). */}
      <h3 style={{ ...panelTitle, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
        <span>{title}</span>
        {slices.length > 0 && (
          <span style={{ color: t.textMain, fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(total)}
          </span>
        )}
      </h3>
      <div style={{ width: '100%', height: 250 }}>
        {slices.length === 0 ? <div style={emptyNote}>{empty}</div> : (
          <ResponsiveContainer>
            <PieChart>
              {/* The donut keeps to the left half and the legend owns a fixed
                  right half. Previously the donut was centred at 40% with a
                  95px radius while the legend reserved no width at all, so the
                  two overlapped on a narrow panel (CHM-149). */}
              <Pie data={slices} cx="27%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value" stroke="none">
                {slices.map((slice, index) => (
                  <Cell key={slice.name} fill={SLICE_COLORS[index % SLICE_COLORS.length]} />
                ))}
              </Pie>
              <text x="27%" y="50%" textAnchor="middle" dominantBaseline="middle">
                <tspan x="27%" dy="-8" fill={t.textMuted} fontSize="11" fontWeight="bold">TOTAL</tspan>
                <tspan x="27%" dy="18" fill={t.textMain} fontSize="14" fontWeight="bold">{compactAxisTick(total)}</tspan>
              </text>
              <Tooltip contentStyle={tooltipStyle} formatter={money} />
              <Legend
                verticalAlign="middle" align="right" layout="vertical"
                wrapperStyle={{
                  fontSize: '12px', width: '46%', right: 0,
                  maxHeight: 230, overflowY: 'auto', lineHeight: '18px'
                }}
                formatter={(value, entry) => (
                  <span style={{ color: t.textMain, marginLeft: '6px' }}>
                    {value}: <strong>{formatCurrency(Number((entry as { payload?: { value?: number } })?.payload?.value))}</strong>
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );

  const healthColour = { ok: t.success, warning: t.warning, over: t.danger, none: t.textFaint };
  const healthLabel = { ok: 'On track', warning: 'Nearly used', over: 'Over budget', none: 'No allotment' };

  const kpiTiles = [
    { label: 'Total Income', sub: comparison(kpi.income, kpi.prevIncome), value: kpi.income, icon: '📈', color: INCOME },
    { label: 'Total Expenses', sub: comparison(kpi.expense, kpi.prevExpense, true), value: kpi.expense, icon: '📉', color: EXPENSE },
    { label: 'Net Balance', sub: comparison(kpi.net, kpi.prevNet), value: kpi.net, icon: '⚖️', color: NET },
    ...(kpi.cashPosition === undefined ? [] : [{
      label: 'Cash Position',
      sub: <span style={{ color: t.textFaint }}>Opening {formatCurrency(kpi.openingBalance)}{kpi.openingBalanceDate ? ` on ${formatDate(kpi.openingBalanceDate)}` : ''} + movements to date</span>,
      value: kpi.cashPosition, icon: '🏦', color: CASH
    }])
  ];

  return (
    <div>
      {/* Charts are SVG: they print, but a chart split down the middle of a
          page break is useless, so each is kept whole. Everything else — page
          size, margins, masthead, footer — comes from the shared surface. */}
      <Notification notification={notification} onClose={dismiss} />

      <div>
      <PageHeader
        icon="📊"
        title="Financial Analytics"
        actions={
          <div style={{ display: 'flex', gap: t.spacing.md, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Select
              label="Financial Year"
              placeholder=""
              options={yearOptions.map((y) => ({ value: y, label: `FY ${y}` }))}
              value={finYear}
              onChange={(e) => setFinYear(e.target.value)}
              containerStyle={{ marginBottom: 0, minWidth: '160px' }}
            />
            <SubTabs
              style={{ marginBottom: 0 }}
              value={period}
              onChange={setPeriod}
              tabs={PERIODS.map((p) => ({ key: p, label: p }))}
            />
          </div>
        }
      />
      </div>

      {/* --- Range and what counts as money --- */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
        <span style={{ fontSize: t.fontSize.sm, color: t.textMuted, fontWeight: 'bold' }}>Range:</span>
        {RANGE_PRESETS.map((r) => (
          <button key={r.key} type="button" onClick={() => setPreset(r.key)} aria-pressed={preset === r.key} style={pill(preset === r.key)}>
            {r.label}
          </button>
        ))}
        {preset === 'custom' && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} containerStyle={{ marginBottom: 0 }} />
            <span style={{ color: t.textMuted, paddingBottom: '10px' }}>to</span>
            <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} containerStyle={{ marginBottom: 0 }} />
          </div>
        )}
        {isSyncing && (
          <span style={{ color: t.info, fontSize: t.fontSize.sm, fontWeight: 'bold', padding: '5px 10px', backgroundColor: t.bgSubtle, borderRadius: '20px' }}>
            ↻ Updating…
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: t.spacing.lg }}>
        <span style={{ fontSize: t.fontSize.sm, color: t.textMuted, fontWeight: 'bold' }}>Also count:</span>
        {SOURCE_OPTIONS.map((opt) => {
          const on = sources.includes(opt.key);
          const amount = sourceTotals[opt.key];
          return (
            <button
              key={opt.key} type="button" onClick={() => toggleSource(opt.key)}
              aria-pressed={on} title={opt.hint} style={pill(on)}
            >
              {on ? '☑' : '☐'} {opt.label}
              {amount !== undefined && (
                <span style={{ fontWeight: 'normal', marginLeft: '6px', color: t.textFaint }}>
                  {opt.kind === 'income' ? '+' : '−'}{formatCurrency(amount)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <p style={{ margin: `0 0 ${t.spacing.lg} 0`, color: t.textMuted, fontSize: t.fontSize.md }}>
        <strong style={{ color: t.textMain }}>{rangeLabel}</strong> — income is donations and collections
        {sources.filter((s) => SOURCE_OPTIONS.find((o) => o.key === s)?.kind === 'income').length ? ' plus the ticked sources' : ''};
        expenses are the expense register{sources.filter((s) => SOURCE_OPTIONS.find((o) => o.key === s)?.kind === 'expense').length ? ' plus the ticked sources' : ''}.
        Comparisons are against {previousLabel}.
      </p>

      {/* --- KPI tiles --- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: t.spacing.lg, marginBottom: t.spacing.xl }}>
        {kpiTiles.map((card) => (
          <KpiCard
            key={card.label}
            label={
              <>
                {card.label}
                <div style={{ fontSize: '11px', fontWeight: t.fontWeight.bold, textTransform: 'none', marginTop: '2px' }}>
                  {card.sub}
                </div>
              </>
            }
            value={formatCurrency(card.value)}
            icon={card.icon}
            color={card.color}
          />
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: t.spacing.xl }}>
        {/* --- Income vs expense trend --- */}
        <div style={panel}>
          <h3 style={panelTitle}>Income vs Expense Trend ({period})</h3>
          <div style={{ width: '100%', height: 350 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="fillIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={INCOME} stopOpacity={0.45} />
                    <stop offset="95%" stopColor={INCOME} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="fillExpense" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={EXPENSE} stopOpacity={0.45} />
                    <stop offset="95%" stopColor={EXPENSE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis dataKey="name" stroke={t.textMuted} tick={axisStyle} />
                <YAxis stroke={t.textMuted} tick={axisStyle} tickFormatter={compactAxisTick} />
                <Tooltip contentStyle={tooltipStyle} formatter={money} />
                <Legend wrapperStyle={{ paddingTop: '20px' }} formatter={legendLabel} />
                <Area type="monotone" dataKey="income" name="Total Income" stroke={INCOME} strokeWidth={3} fillOpacity={1} fill="url(#fillIncome)" activeDot={{ r: 6 }} />
                <Area type="monotone" dataKey="expense" name="Total Expense" stroke={EXPENSE} strokeWidth={3} fillOpacity={1} fill="url(#fillExpense)" activeDot={{ r: 6 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* --- Net per bucket and the running balance --- */}
        <div style={panel}>
          <h3 style={panelTitle}>Surplus / Deficit and Running Balance ({period})</h3>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                <XAxis dataKey="name" stroke={t.textMuted} tick={axisStyle} />
                <YAxis stroke={t.textMuted} tick={axisStyle} tickFormatter={compactAxisTick} />
                <Tooltip cursor={cursorFill} contentStyle={tooltipStyle} formatter={money} />
                <Legend formatter={legendLabel} />
                <ReferenceLine y={0} stroke={t.borderStrong} />
                <Bar dataKey="net" name="Surplus / Deficit" radius={[4, 4, 0, 0]}>
                  {chartData.map((point) => (
                    <Cell key={point.name} fill={(point.net ?? 0) >= 0 ? INCOME : EXPENSE} />
                  ))}
                </Bar>
                <Line type="monotone" dataKey="cumulative" name="Running balance" stroke={NET} strokeWidth={3} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: t.spacing.xl }}>
          {/* --- Donations vs collections --- */}
          <div style={panel}>
            <h3 style={panelTitle}>Donations vs Collections</h3>
            <div style={{ width: '100%', height: 250 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                  <XAxis dataKey="name" stroke={t.textMuted} tick={axisStyle} />
                  <YAxis stroke={t.textMuted} tick={axisStyle} tickFormatter={compactAxisTick} />
                  <Tooltip cursor={cursorFill} contentStyle={tooltipStyle} formatter={money} />
                  <Legend formatter={legendLabel} />
                  <Bar dataKey="donations" name="Donations" fill={DONATIONS} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="collections" name="Collections" fill={COLLECTIONS} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {donut('Income Breakdown', incomeData, totalIncomePie, 'No income recorded in this range.')}
          {donut('Expense Breakdown', pieData, totalExpensePie, 'No expenses recorded in this range.')}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: t.spacing.xl }}>
          {topList('Top Expense Categories', pieData, EXPENSE, 'No expenses in this range.')}
          {topList('Top Income Sources', incomeData, INCOME, 'No income in this range.')}
          {topList('Top Payees', topPayees, COLLECTIONS, 'No expense names a payee yet — fill "Paid To" on the expense form.')}
        </div>

        <h2 style={{ ...styles.headerTitle, marginTop: t.spacing.md }}>Budget Analytics — FY {finYear}</h2>
        <p style={{ margin: `-${t.spacing.md} 0 0 0`, color: t.textMuted, fontSize: t.fontSize.sm }}>
          Spent is counted from the expense register: every expense whose category is the budget head.
          The same figures as Budget Management, always for the financial year above whatever range is chosen.
        </p>

        {/* --- Budget health table --- */}
        <div style={panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <h3 style={{ ...panelTitle, margin: 0 }}>Budget Health by Head</h3>
          </div>
          {hasNoBudgetData(budgetHeadsData) ? (
            <div style={emptyNote}>No budget prepared for FY {finYear}. Add heads under Budget Management › Annual Budget.</div>
          ) : (
            <div className="data-table-scroll" style={{ overflowX: 'auto', marginTop: t.spacing.md }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: t.fontSize.md }}>
                <thead>
                  <tr style={{ color: t.textMuted, fontSize: t.fontSize.sm, textAlign: 'left' }}>
                    {['Budget head', 'Allotted', 'Spent', 'Remaining', 'Used', 'Status'].map((h) => (
                      <th key={h} style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}`, textAlign: ['Allotted', 'Spent', 'Remaining'].includes(h) ? 'right' : 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {budgetHeadsData.map((head) => {
                    const health = budgetHealth(head);
                    const remaining = head.allotted - head.spent;
                    const pct = head.allotted ? (head.spent / head.allotted) * 100 : 0;
                    return (
                      <tr key={head.name} style={{ borderBottom: `1px solid ${t.border}` }}>
                        <td style={{ padding: '10px', color: t.textMain, fontWeight: 'bold' }}>{head.name}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: t.textMain }} className="tabular-nums">{formatCurrency(head.allotted)}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: EXPENSE, fontWeight: 'bold' }} className="tabular-nums">{formatCurrency(head.spent)}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: remaining < 0 ? t.danger : t.success, fontWeight: 'bold' }} className="tabular-nums">{formatCurrency(remaining)}</td>
                        <td style={{ padding: '10px', minWidth: '160px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1, backgroundColor: t.bgMain, height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{ width: `${spentPercent(head)}%`, height: '100%', backgroundColor: healthColour[health] }} />
                            </div>
                            <span style={{ width: '48px', textAlign: 'right', color: t.textMuted, fontSize: t.fontSize.sm }} className="tabular-nums">
                              {head.allotted ? `${pct.toFixed(0)}%` : '—'}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: t.radius.pill, fontSize: t.fontSize.xs, fontWeight: 'bold',
                            color: '#fff', backgroundColor: healthColour[health]
                          }}>
                            {healthLabel[health]}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 'bold', color: t.textMain }}>
                    <td style={{ padding: '10px' }}>Total</td>
                    <td style={{ padding: '10px', textAlign: 'right' }} className="tabular-nums">{formatCurrency(budgetHeadsData.reduce((s, h) => s + h.allotted, 0))}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: EXPENSE }} className="tabular-nums">{formatCurrency(budgetHeadsData.reduce((s, h) => s + h.spent, 0))}</td>
                    <td style={{ padding: '10px', textAlign: 'right' }} className="tabular-nums">{formatCurrency(budgetHeadsData.reduce((s, h) => s + h.allotted - h.spent, 0))}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* --- Year over year --- */}
        <div style={{ ...panel, height: '400px' }}>
          <h3 style={panelTitle}>Year-over-Year: Total Budget vs Actual Expenses</h3>
          <div style={{ width: '100%', height: '85%' }}>
            <ResponsiveContainer>
              <BarChart data={yoyBudgetData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                <XAxis dataKey="year" stroke={t.textMuted} tick={axisStyle} />
                <YAxis stroke={t.textMuted} tick={axisStyle} tickFormatter={compactAxisTick} />
                <Tooltip cursor={cursorFill} contentStyle={tooltipStyle} formatter={money} />
                <Legend wrapperStyle={{ paddingTop: '10px' }} formatter={legendLabel} />
                <Bar dataKey="budget" name="Total Budget" fill={INCOME} radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" name="Actual Expenses (register)" fill={EXPENSE} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialDashboard;
